import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * useReportDownload — robust report download with content-type sniffing,
 * mobile Safari fallback, and toast notifications instead of alert().
 */
export default function useReportDownload(apiBase, user) {
  const [downloadingReport, setDownloadingReport] = useState(false);

  // Detect iOS Safari for blob download fallback
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    return /iP(ad|hone|od)/.test(ua) && /WebKit/.test(ua) && !/CriOS|FxiOS/.test(ua);
  };

  const handleDownloadReport = useCallback(async (requestedSessionId = null) => {
    const sessionId = requestedSessionId || sessionStorage.getItem('mockbot_session_id');
    console.log('📄 Attempting to download report for session:', sessionId);

    if (!sessionId) {
      toast.error('No interview session found. Please complete an interview first.');
      return;
    }

    setDownloadingReport(true);

    try {
      const token = await user.getIdToken();

      const response = await fetch(`${apiBase}/generate_report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_id: sessionId })
      });

      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/pdf')) {
        // =========== PDF received — download it ===========
        const blob = await response.blob();

        if (!blob || blob.size === 0) {
          toast.error('Downloaded file is empty. Please try again.');
          return;
        }

        console.log(`📥 PDF blob size: ${blob.size} bytes`);

        const url = window.URL.createObjectURL(blob);

        if (isIOSSafari()) {
          // Mobile Safari can't programmatically click <a download>
          window.open(url, '_blank');
          toast.success('Report opened in new tab. Long-press to save.');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = `Vagmee_Interview_Report_${sessionId}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Revoke after small delay to let browser start the download
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
          toast.success('Report downloaded!');
        }
      } else {
        // =========== JSON response — check evaluation status ===========
        const data = await response.json().catch(() => ({}));
        console.log('📊 Report response (non-PDF):', data);

        if (
          data.evaluation_status === 'PENDING' ||
          data.evaluation_status === 'EVALUATING'
        ) {
          toast('Report is still being generated. Please wait and try again.', {
            icon: '⏳',
            duration: 5000
          });
        } else if (data.evaluation_status === 'NOT_FOUND') {
          toast.error('Evaluation not found. Please stop the interview first, then download.');
        } else if (data.evaluation_status === 'FAILED') {
          toast.error(`Report generation failed: ${data.error || 'Unknown error'}`);
        } else if (response.status >= 500) {
          toast.error('Server error generating report. Please try again later.');
        } else {
          toast.error(data.error || 'Unable to generate report. Please contact support.');
        }
      }
    } catch (err) {
      console.error('❌ Download report error:', err);
      toast.error('Download failed. Check your connection and try again.');
    } finally {
      setDownloadingReport(false);
    }
  }, [apiBase, user]);

  return { downloadingReport, handleDownloadReport };
}
